import type { Message } from '../../types/message';
import { MessageBubble } from './MessageBubble';
import { MessageSkeleton } from './MessageSkeleton';
import { formatDateDivider } from '../../utils/formatters';
import {
  hasMoreLocalMessagesSignal,
  isLoadingOlderMessagesSignal,
  isSyncingHistorySignal,
  noMoreCloudMessagesSignal
} from '../../state/messages.store';
import { activeContactSignal, syncingContactsSignal, syncHistoryProgressSignal, triggerSyncHistory, isLoadingContactDetailsSignal, isLoadingMessagesSignal } from '../../state/contacts.store';
import { Cloud, Loader2, CheckCircle2, AlertCircle, RefreshCw } from 'lucide-preact';

interface MessageListProps {
  messages: Message[];
  isGroup?: boolean;
  onOpenVisionModal?: (mediaUrl: string, content: string, messageId?: string) => void;
}

export function MessageList({ messages, isGroup, onOpenVisionModal }: MessageListProps) {
  const activeContact = activeContactSignal.value;
  const activePhone = activeContact?.phone_number ? activeContact.phone_number.replace(/\D/g, '') : '';
  const hasMoreLocal = hasMoreLocalMessagesSignal.value;
  const isLoadingOlder = isLoadingOlderMessagesSignal.value;
  const isSyncingHistory = isSyncingHistorySignal.value || (activePhone ? Boolean(syncingContactsSignal.value[activePhone]) : false);
  const progressInfo = activePhone ? syncHistoryProgressSignal.value[activePhone] : null;
  const pct = Math.min(Math.max(5, progressInfo?.progress || 5), 100);
  const noMoreCloud = noMoreCloudMessagesSignal.value;
  const isLoadingContactDetails = isLoadingContactDetailsSignal.value;
  const isLoadingMessages = isLoadingMessagesSignal.value;

  const handleSyncEvolution = () => {
    if (activeContact) {
      triggerSyncHistory(activeContact.phone_number);
    }
  };

  if (isLoadingContactDetails || isLoadingMessages) {
    return <MessageSkeleton />;
  }

  if (messages.length === 0) {
    return (
      <div class="h-full flex flex-col items-center justify-center text-center p-8 text-slate-500 space-y-3">
        <p class="text-sm font-semibold text-slate-400">Nenhuma mensagem salva no banco local para este contato.</p>
        {isSyncingHistory ? (
          <div class="w-full max-w-xs p-4 bg-slate-900/80 border border-cyan-500/40 rounded-2xl shadow-lg space-y-2">
            <div class="flex items-center justify-between text-xs font-bold text-cyan-300">
              <span class="flex items-center gap-2">
                <Loader2 class="w-4 h-4 text-cyan-400 animate-spin shrink-0" />
                <span>Sincronizando WhatsApp...</span>
              </span>
              <span class="font-mono text-cyan-400">{pct}%</span>
            </div>
            <div class="w-full bg-slate-800 rounded-full h-2.5 overflow-hidden border border-cyan-500/30">
              <div
                class="bg-gradient-to-r from-cyan-500 via-blue-500 to-indigo-500 h-full transition-[width] duration-300 rounded-full"
                style={{ width: `${pct}%` }}
              />
            </div>
            {progressInfo && progressInfo.totalSynced > 0 && (
              <p class="text-[11px] text-slate-400 font-mono">
                {progressInfo.totalSynced} mensagens recuperadas até agora
              </p>
            )}
          </div>
        ) : (
          <button
            onClick={handleSyncEvolution}
            class="px-4 py-2 rounded-xl border text-xs font-bold transition flex items-center gap-2 shadow-sm bg-cyan-600/20 hover:bg-cyan-600/30 text-cyan-300 border-cyan-500/40 cursor-pointer"
          >
            <Cloud class="w-4 h-4 text-cyan-400 shrink-0" />
            <span>Buscar Histórico no WhatsApp (Evolution API)</span>
          </button>
        )}
      </div>
    );
  }

  // Group messages by date
  const grouped: { dateDivider: string; msgs: Message[] }[] = [];
  let currentDateDivider = '';

  messages.forEach(msg => {
    const d = new Date(msg.timestamp);
    const divider = isNaN(d.getTime()) ? '' : formatDateDivider(d);

    if (divider !== currentDateDivider) {
      currentDateDivider = divider;
      grouped.push({ dateDivider: divider, msgs: [msg] });
    } else {
      grouped[grouped.length - 1].msgs.push(msg);
    }
  });

  return (
    <div class="space-y-1 p-2 pr-[max(0.5rem,env(safe-area-inset-right))] sm:p-3 sm:pr-4">
      {/* Top Controls: Auto Loading Spinner or Cloud Sync Button when Local History Ends */}
      <div class="flex flex-col items-center justify-center my-3 text-center">
        {isLoadingOlder ? (
          <div class="flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-900/90 border border-cyan-500/30 text-cyan-300 text-xs font-mono shadow-md animate-pulse">
            <Loader2 class="w-3.5 h-3.5 animate-spin text-cyan-400" />
            <span>Carregando mensagens anteriores do banco local...</span>
          </div>
        ) : !hasMoreLocal ? (
          <div class="flex flex-col items-center gap-2 p-3 bg-slate-900/60 rounded-2xl border border-slate-800/80 shadow-sm max-w-md w-full">
            <span class="text-[11px] text-slate-400 font-medium">Início do histórico local alcançado.</span>
            {noMoreCloud ? (
              <div class="flex items-center gap-1.5 text-xs font-medium text-emerald-400 bg-emerald-950/40 border border-emerald-500/30 px-3 py-1.5 rounded-xl">
                <CheckCircle2 class="w-4 h-4 text-emerald-400 shrink-0" />
                <span>Histórico completo da nuvem sincronizado!</span>
              </div>
            ) : progressInfo?.status === 'FAILED' ? (
              <div class="w-full p-3 bg-red-950/60 border border-red-500/50 rounded-xl space-y-2 text-left animate-fade-in">
                <div class="flex items-center justify-between text-xs font-bold text-red-300">
                  <span class="flex items-center gap-1.5">
                    <AlertCircle class="w-4 h-4 text-red-400 shrink-0" />
                    <span>Falha na Sincronização</span>
                  </span>
                  <button
                    onClick={handleSyncEvolution}
                    class="px-2.5 py-1 bg-red-500/20 hover:bg-red-500/30 text-red-200 border border-red-500/40 text-[11px] font-bold rounded-lg transition cursor-pointer flex items-center gap-1 shadow-sm"
                  >
                    <RefreshCw class="w-3 h-3 text-red-300" />
                    <span>Tentar Novamente</span>
                  </button>
                </div>
                <p class="text-[11px] text-red-200/90 leading-tight font-medium">
                  {(progressInfo as any).error || 'Falha de comunicação com a API do WhatsApp.'}
                </p>
              </div>
            ) : isSyncingHistory ? (
              <div class="w-full p-3 bg-slate-950/80 border border-cyan-500/40 rounded-xl space-y-2">
                <div class="flex items-center justify-between text-xs font-bold text-cyan-300">
                  <span class="flex items-center gap-2">
                    <Loader2 class="w-4 h-4 text-cyan-400 animate-spin shrink-0" />
                    <span>Sincronizando com WhatsApp...</span>
                  </span>
                  <span class="font-mono text-cyan-400">{pct}%</span>
                </div>
                <div class="w-full bg-slate-800 rounded-full h-2 overflow-hidden border border-cyan-500/30">
                  <div
                    class="bg-gradient-to-r from-cyan-500 via-blue-500 to-indigo-500 h-full transition-[width] duration-300 rounded-full"
                    style={{ width: `${pct}%` }}
                  />
                </div>
                {progressInfo && progressInfo.totalSynced > 0 && (
                  <p class="text-[10px] text-slate-400 font-mono text-right">
                    {progressInfo.totalSynced} mensagens salvas
                  </p>
                )}
              </div>
            ) : (
              <button
                onClick={handleSyncEvolution}
                class="px-4 py-2 rounded-xl border text-xs font-bold transition flex items-center gap-2 shadow-sm bg-cyan-600/20 hover:bg-cyan-600/30 text-cyan-300 border-cyan-500/40 cursor-pointer"
              >
                <Cloud class="w-4 h-4 text-cyan-400 shrink-0" />
                <span>Carregar Histórico Antigo da Nuvem (WhatsApp / Evolution)</span>
              </button>
            )}
          </div>
        ) : null}
      </div>

      {grouped.map((group, idx) => (
        <div key={idx} class="space-y-0.5">
          {group.dateDivider && (
            <div class="flex justify-center my-1.5 sm:my-2">
              <span class="px-2.5 py-0.5 bg-slate-900/90 border border-slate-800 text-[10px] font-mono text-cyan-300 rounded-full shadow-sm">
                {group.dateDivider}
              </span>
            </div>
          )}

          {group.msgs.map((msg, msgIdx) => {
            const prevMsg = msgIdx > 0 ? group.msgs[msgIdx - 1] : null;

            let isConsecutive = false;
            if (prevMsg && isGroup && msg.sender_type === 'contact' && prevMsg.sender_type === 'contact') {
              const prevSender = (prevMsg.sender_name || prevMsg.participant_jid || '').trim();
              const currSender = (msg.sender_name || msg.participant_jid || '').trim();

              const prevTime = new Date(prevMsg.timestamp).getTime();
              const currTime = new Date(msg.timestamp).getTime();
              const isWithinTenMinutes = !isNaN(prevTime) && !isNaN(currTime) && Math.abs(currTime - prevTime) < 10 * 60 * 1000;

              if (prevSender && currSender && prevSender === currSender && isWithinTenMinutes) {
                isConsecutive = true;
              }
            }

            const showSenderName = !isConsecutive;

            return (
              <MessageBubble
                key={msg.id || msg.message_id}
                message={msg}
                isGroup={isGroup}
                showSenderName={showSenderName}
                onOpenVisionModal={onOpenVisionModal}
              />
            );
          })}
        </div>
      ))}
    </div>
  );
}
